import React__default, { createContext, useMemo, useContext } from 'react';
import { useMedia } from '@shopify/react-hooks';
import { mediaQueries } from './media-queries.mjs';
import { MediaQueryTarget } from './types.mjs';
import { useFeatures } from '../FeaturesProvider/FeaturesProvider.mjs';

const DEFAULT_MIN_RANGE = {
  [MediaQueryTarget.Phablet]: false,
  [MediaQueryTarget.Tablet]: false,
  [MediaQueryTarget.Desktop]: false,
  [MediaQueryTarget.Widescreen]: false
};
const DEFAULT_MAX_RANGE = {
  [MediaQueryTarget.Mobile]: false,
  // Candidates for removal
  [MediaQueryTarget.MobileCommon]: false
};
const DEFAULT_DISCRETE = {
  [MediaQueryTarget.Landscape]: false,
  [MediaQueryTarget.PrefersReducedMotion]: false
};
const DEFAULT_CONTEXT = {
  ...DEFAULT_MIN_RANGE,
  ...DEFAULT_MAX_RANGE,
  ...DEFAULT_DISCRETE,
  betweenBreakpoints(_min, _max) {
    return false;
  }
};
const MediaQueryContext = /*#__PURE__*/createContext(DEFAULT_CONTEXT);
function MediaQueryProvider({
  children
}) {
  const {
    disablesRightSidebar
  } = useFeatures();
  const phablet = useMedia(mediaQueries.phablet);
  const tablet = useMedia(mediaQueries.tablet);
  const desktop = useMedia(mediaQueries.desktop);
  const widescreen = useMedia(mediaQueries.widescreen);

  // TODO: Simplify the below `useMedia` with `!phablet`.
  const mobile = useMedia(mediaQueries.mobile);
  // Candidates for removal
  const mobileCommon = useMedia(mediaQueries.mobileCommon);
  const landscape = useMedia(mediaQueries.landscape);
  const prefersReducedMotion = useMedia(mediaQueries.prefersReducedMotion);
  const value = useMemo(() => {
    const queries = {
      mobile,
      phablet,
      tablet,
      desktop,
      widescreen: disablesRightSidebar ? false : widescreen,
      landscape,
      prefersReducedMotion,
      mobileCommon
    };
    return {
      ...queries,
      betweenBreakpoints(min, max) {
        const minWidth = min === 'mobile' || queries[min];
        // NOTE: We safely check against `min` and `max` being the same value.
        // But we do not check if `min` is a later breakpoint than `max`.
        const singleBreakpoint = min === max;
        return singleBreakpoint ? queries[min] : minWidth && !queries[max];
      }
    };
  }, [mobile, phablet, tablet, desktop, widescreen, landscape, prefersReducedMotion, mobileCommon, disablesRightSidebar]);
  return /*#__PURE__*/React__default.createElement(MediaQueryContext.Provider, {
    value: value
  }, children);
}
function useMediaQuery() {
  return useContext(MediaQueryContext);
}

export { DEFAULT_CONTEXT, MediaQueryContext, MediaQueryProvider, useMediaQuery };
